// =============================================================================
// clients-client.http.ts — Adaptateur HTTP RÉEL du ClientsClient (référentiel clients, S6).
// =============================================================================
// Remplace le stub-first derrière le MÊME DI seam (ClientsClientProvider) : les hooks/
// écrans consomment l'interface ClientsClient, l'impl est substituée sans toucher l'UI.
//
// AUTORITÉ = API (NestJS, AdminSessionGuard, émission DE.Client*, transitions Actif↔Archive,
// no-DELETE §3.3.5 inv.3, unicité SIREN). Cet adaptateur ne porte AUCUNE règle métier : il
// sérialise l'intention, parse la réponse et relaie le message d'erreur serveur.
// INVARIANT : le SIREN d'archive/reactivate part TOUJOURS dans l'URL (jamais le body).
//
// SÉCURITÉ (RULE_REF_006) : la session admin est portée par le COOKIE httpOnly posé par
// l'API (`credentials: 'include'`). CSRF DOUBLE-SUBMIT : les mutations (POST create/archive/
// reactivate) ré-émettent le cookie NON-httpOnly `admin_csrf` en en-tête `x-csrf-token`
// (AdminSessionGuard l'exige ; sans lui = 403). Le GET (list) ne porte PAS de CSRF.
// Aucune donnée métier en localStorage.

import {
  ClientsApiError,
  type ClientCampaignHistoryItemDto,
  type ClientDetailDto,
  type ClientListItemDto,
  type ClientsClient,
  type CreateClientInput,
  type CreateClientResult,
  type EditClientInput,
} from '@/api/clients-client';

/** Forme du corps d'erreur structuré NestJS (`{ statusCode, code?, message }`). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly code?: string;
  readonly message?: string;
}

/** Nom du cookie CSRF NON-httpOnly posé au login admin (double-submit, cf. API CSRF_COOKIE). */
const CSRF_COOKIE_NAME = 'admin_csrf';

/** Lit la valeur du cookie CSRF `admin_csrf` (NON-httpOnly, lisible par le SPA même origine). */
function readCsrfToken(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === CSRF_COOKIE_NAME) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

/**
 * Adaptateur HTTP réel du référentiel clients admin. `baseUrl` par défaut vide (chemins
 * relatifs : SPA et API partagent l'origine, ou proxy). En test, fetch est mocké.
 */
export class HttpClientsClient implements ClientsClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  public async listClients(): Promise<readonly ClientListItemDto[]> {
    return this.request<ClientListItemDto[]>(
      '/admin/clients',
      { method: 'GET' },
      true,
    );
  }

  public async getClientDetail(siren: string): Promise<ClientDetailDto> {
    // Lecture seule (GET) : cookie de session admin via credentials, PAS de CSRF (double-submit réservé
    // aux mutations). 404 → ClientsApiError (client introuvable) via le chemin d'erreur commun.
    return this.request<ClientDetailDto>(
      `/admin/clients/${encodeURIComponent(siren)}`,
      { method: 'GET' },
      true,
    );
  }

  public async getClientCampaigns(
    siren: string,
  ): Promise<readonly ClientCampaignHistoryItemDto[]> {
    // Chronologie cross-BC (campagnes du client). JAMAIS 404 : [] si aucune campagne. Tri « plus récente
    // d'abord » = autorité SERVEUR ; l'UI ne re-trie pas.
    return this.request<ClientCampaignHistoryItemDto[]>(
      `/admin/clients/${encodeURIComponent(siren)}/campaigns`,
      { method: 'GET' },
      true,
    );
  }

  public async createClient(input: CreateClientInput): Promise<CreateClientResult> {
    return this.request<CreateClientResult>(
      '/admin/clients',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  }

  public async editClient(input: EditClientInput): Promise<void> {
    // SIREN dans l'URL (invariant, jamais body). Le reste du corps = les attributs à éditer + le bloc
    // contact référent éventuel. Mutation ⇒ CSRF double-submit (x-csrf-token). Corps vide en réponse.
    const { siren, ...changes } = input;
    await this.request<void>(
      `/admin/clients/${encodeURIComponent(siren)}`,
      { method: 'PATCH', body: JSON.stringify(changes) },
      false,
    );
  }

  public async archiveClient(siren: string): Promise<void> {
    // SIREN dans l'URL (invariant). Corps vide : la ressource est ciblée par son id d'URL.
    await this.request<void>(
      `/admin/clients/${encodeURIComponent(siren)}/archive`,
      { method: 'POST', body: JSON.stringify({}) },
      false,
    );
  }

  public async reactivateClient(siren: string): Promise<void> {
    await this.request<void>(
      `/admin/clients/${encodeURIComponent(siren)}/reactivate`,
      { method: 'POST', body: JSON.stringify({}) },
      false,
    );
  }

  /**
   * Exécute une requête same-origin (cookie httpOnly), parse le JSON typé si `expectBody`,
   * ou lève une ClientsApiError. Sur mutation (POST), ré-émet le cookie `admin_csrf` en
   * en-tête `x-csrf-token` (double-submit) ; les GET n'en portent pas.
   */
  private async request<T>(
    path: string,
    init: RequestInit,
    expectBody: boolean,
  ): Promise<T> {
    const method = (init.method ?? 'GET').toUpperCase();
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (method !== 'GET' && method !== 'HEAD') {
      const csrf = readCsrfToken();
      if (csrf !== undefined) {
        headers['x-csrf-token'] = csrf;
      }
    }
    const response = await fetch(`${this.baseUrl}${path}`, {
      credentials: 'include',
      ...init,
      headers: { ...headers, ...init.headers },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    if (!expectBody) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  /** Convertit une réponse d'erreur en ClientsApiError (message serveur, sinon repli). */
  private async toApiError(response: Response): Promise<ClientsApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur le référentiel clients.`;
    return new ClientsApiError(message, response.status);
  }
}
